import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import constants from '../../config/constants'

class AutocompleteSuggestions extends Component {
  wrapperRef = React.createRef()
  suggestionListRef = React.createRef()
  focusedSuggestionRef = React.createRef()

  state = {
    position: '',
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      items: oldItems,
      focusedSuggestion: oldFocusedSuggestion,
    } = prevProps

    const {
      items: newItems,
      focusedSuggestion: newFocusedSuggestion,
      velocity,
    } = this.props

    if (
      !newItems.length ||
      !oldItems.length ||
      newItems.length !== oldItems.length ||
      prevProps.isOpen === false
    ) {
      this.resetScroll()
      const position = this.positionComponent()

      if (position && position !== this.state.position) {
        this.setState({ position })
      }
    }

    // Scroll the suggestionlist based off the
    // focused sugesstion it's position
    if (newFocusedSuggestion !== oldFocusedSuggestion) {
      this.setScrollOffset(velocity)
    }
  }

  resetScroll() {
    const suggestionsEl = this.suggestionListRef.current
    if (!suggestionsEl) return

    suggestionsEl.scrollTop = 0
  }

  setScrollOffset(velocity) {
    const suggestionsEl = this.suggestionListRef.current
    const focusedSuggestionEl = this.focusedSuggestionRef.current

    if (!suggestionsEl || !focusedSuggestionEl) return

    const { height, top } = suggestionsEl.getBoundingClientRect()
    const {
      top: focusedTop,
      height: focusedHeight,
    } = focusedSuggestionEl.getBoundingClientRect()

    if (velocity > 0) {
      const offset = focusedTop + focusedHeight
      if (offset > height + top || offset < top) {
        suggestionsEl.scrollTop = focusedSuggestionEl.offsetTop
      }
    } else if (velocity < 0) {
      const offset = focusedTop - focusedHeight
      if (offset < top - focusedHeight || offset > height + top) {
        suggestionsEl.scrollTop =
          focusedSuggestionEl.offsetTop - (height - focusedHeight)
      }
    }
  }

  /**
   * Position the suggestions list  based on the visibility in the viewport.
   * @return {string} Return the position of the menu: "below", "above"
   */
  positionComponent() {
    const wrapperEl = this.wrapperRef.current
    if (!wrapperEl) return

    const maxBoundY = window.innerHeight

    const { height: wrapperHeight } = wrapperEl.getBoundingClientRect()
    const { y: inputY, height: inputHeight } = this.props.inputDimension

    const offsetY =
      inputY + inputHeight + constants.autoCompleteSSuggestionPadding

    if (Math.round(offsetY + wrapperHeight) > maxBoundY) {
      // Suggestions are rendered above the input field
      wrapperEl.style.top = `-${wrapperHeight}px`
      return 'above'
    } else {
      // Suggestions are rendered below the input field
      wrapperEl.style.top = `calc(${inputHeight}px)`
      return 'below'
    }
  }

  render() {
    const { position } = this.state
    const { classes, isOpen, items, focusedSuggestion } = this.props
    if (!isOpen || !items.length) return null

    return (
      <section
        ref={this.wrapperRef}
        className={classNames(classes.wrapper, classes.arrow, {
          [classes.arrowBelow]: position === 'below',
          [classes.arrowAbove]: position === 'above',
        })}
      >
        <ul
          ref={this.suggestionListRef}
          className={classes.suggestions}
          data-element="autocomplete"
        >
          {items.map(({ label, emphasized }, index) => (
            <li
              ref={
                focusedSuggestion === index ? this.focusedSuggestionRef : null
              }
              key={label}
              onClick={this.props.onFocusSuggestion(index)}
              className={classNames(classes.suggestion, {
                [classes.focused]: focusedSuggestion === index,
              })}
              data-element={`autocomplete-item-${label}`}
            >
              {emphasized || label}
            </li>
          ))}
        </ul>
      </section>
    )
  }
}

AutocompleteSuggestions.propTypes = {
  classes: PropTypes.shape({
    wrapper: PropTypes.string.isRequired,
    arrowAbove: PropTypes.string.isRequired,
    arrowBelow: PropTypes.string.isRequired,
    suggestions: PropTypes.string.isRequired,
    suggestion: PropTypes.string.isRequired,
    focused: PropTypes.string.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  focusedSuggestion: PropTypes.number.isRequired,
  onFocusSuggestion: PropTypes.func.isRequired,
  inputDimension: PropTypes.shape({
    y: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  velocity: PropTypes.number,
  isOpen: PropTypes.bool,
}

AutocompleteSuggestions.defaultProps = {
  isCaseSensitive: false,
  velocity: 0,
  isOpen: true,
}

export default AutocompleteSuggestions
